import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FeedFavoriteButton } from './FeedFavoriteButton';
import { useFeedFavorites } from '@/hooks/useFeedFavorites';
import { useI18n } from '@/hooks/useI18n';
import { useInterval } from '@/hooks/useInterval';
import { usePrefersReducedMotion } from '@/hooks/usePrefersReducedMotion';
import { useShell } from '@/hooks/useShell';
import { matchesFeedTag, normalizeFeedTag } from '@/lib/feedTagFilter';
import { routes } from '@/lib/routes';

const ROTATE_MS = 7000;
const SLIDE_MS = 550;

type FeedCard = {
  id: string;
  num: string;
  source: string;
  title: string;
  action: string;
  href: string;
};

/** Рабочая лента: cyclic advance even when content fits the viewport (Task 14). */
export const FeedPanel = () => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { shell } = useShell();
  const reducedMotion = usePrefersReducedMotion();
  const { isFavorite, toggleFavorite, canFavorite } = useFeedFavorites();
  const [paused, setPaused] = useState(false);
  const [sliding, setSliding] = useState(false);
  const lockRef = useRef(false);

  const activeTag = normalizeFeedTag(params.get('tag') || params.get('q'));
  const apiItems = shell?.work_feed ?? [];

  const filteredApi = useMemo(
    () =>
      apiItems.filter((item) =>
        matchesFeedTag(activeTag, [item.kind, item.title, item.summary], item.tags ?? []),
      ),
    [apiItems, activeTag],
  );

  const [items, setItems] = useState<FeedCard[]>([]);

  useEffect(() => {
    setItems(
      filteredApi.map((item, position) => ({
        id: item.id,
        num: String(item.order_no || position + 1).padStart(2, '0'),
        source: item.kind,
        title: item.title,
        action: item.summary,
        href: item.href || routes.requests,
      })),
    );
  }, [filteredApi]);

  const advance = useCallback(() => {
    if (lockRef.current || items.length < 2) return;
    lockRef.current = true;
    if (reducedMotion) {
      setItems((prev) => [...prev.slice(1), prev[0]]);
      lockRef.current = false;
      return;
    }
    setSliding(true);
    window.setTimeout(() => {
      setItems((prev) => [...prev.slice(1), prev[0]]);
      setSliding(false);
      lockRef.current = false;
    }, SLIDE_MS);
  }, [items.length, reducedMotion]);

  useInterval(advance, paused || reducedMotion || items.length < 2 ? null : ROTATE_MS);

  return (
    <aside className="panel">
      <div className="panel-title">
        <span>{t('feed.title')}</span>
        <span className="badge">
          {activeTag ? `#${activeTag} · ${items.length}` : items.length || t('feed.badge')}
        </span>
      </div>
      <div
        className="left-scroll"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onFocusCapture={() => setPaused(true)}
        onBlurCapture={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
            setPaused(false);
          }
        }}
        onPointerDown={() => setPaused(true)}
      >
        {items.length === 0 ? (
          <p className="public-empty">
            {activeTag ? `Нет карточек по #${activeTag}` : 'Лента пока пуста'}
          </p>
        ) : (
          <div className={`left-track${sliding ? ' is-advancing' : ''}`}>
            {items.map((item) => (
              <div key={item.id} className="left-card-wrap">
                <button
                  type="button"
                  className="left-card"
                  onClick={() => navigate(item.href)}
                >
                  <span className="left-num">{item.num}</span>
                  <span>
                    <small>
                      {item.source}
                      <FeedFavoriteButton
                        itemId={item.id}
                        isFavorite={isFavorite(item.id)}
                        disabled={!canFavorite}
                        onToggle={toggleFavorite}
                      />
                    </small>
                    <h4>{item.title}</h4>
                    <p>{item.action}</p>
                    <em className="left-open">Открыть →</em>
                  </span>
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </aside>
  );
};
